'use client';

import { useState, useCallback, useMemo } from 'react';
import type {
  UseInvoiceStateReturn,
  InvoiceStatusFilter,
  InvoiceFilters,
  InvoiceTabState,
} from './types';
import { DEFAULT_MAX_AGE_DAYS } from '../PeriodFilter/PeriodFilter';
import { DEFAULT_STATUS } from './constants';

const createDefaultTabState = (status: InvoiceStatusFilter): InvoiceTabState => ({
  filters: {
    status,
    maxAgeDays: DEFAULT_MAX_AGE_DAYS,
  },
  pagination: {
    keys: [],
    hasMore: false,
  },
});

const createInitialTabStates = () => ({
  Open: createDefaultTabState('Open'),
  All: createDefaultTabState('All'),
});

export const useInvoiceState = (enableTabsMode: boolean): UseInvoiceStateReturn => {
  const [activeTab, setActiveTab] = useState<InvoiceStatusFilter>(DEFAULT_STATUS);
  const [tabStates, setTabStates] = useState(() => createInitialTabStates());

  const updateFilters = useCallback(
    (filters: Partial<InvoiceFilters>, tab?: InvoiceStatusFilter) => {
      const targetTab = tab || activeTab;

      // Update filters for the specified tab, keeping other state intact. If state gets more complex, consider using a state management library
      setTabStates((prev) => ({
        ...prev,
        [targetTab]: {
          ...prev[targetTab],
          filters: {
            ...prev[targetTab].filters,
            ...filters,
          },
          // Reset pagination when filters change
          pagination: {
            keys: [],
            hasMore: false,
          },
        },
      }));
    },
    [activeTab],
  );

  const addPaginationKey = useCallback(
    (key: string, tab?: InvoiceStatusFilter) => {
      const targetTab = tab || activeTab;

      setTabStates((prev) => ({
        ...prev,
        [targetTab]: {
          ...prev[targetTab],
          pagination: {
            ...prev[targetTab].pagination,
            keys: [...prev[targetTab].pagination.keys, key],
          },
        },
      }));
    },
    [activeTab],
  );

  const resetTab = useCallback((tab: InvoiceStatusFilter) => {
    setTabStates((prev) => ({
      ...prev,
      [tab]: createDefaultTabState(tab),
    }));
  }, []);

  const getCurrentFilters = useCallback(() => tabStates[activeTab].filters, [tabStates, activeTab]);
  const getCurrentPaginationKeys = useCallback(
    () => tabStates[activeTab].pagination.keys,
    [tabStates, activeTab],
  );
  const getTabState = useCallback((tab: InvoiceStatusFilter) => tabStates[tab], [tabStates]);

  return useMemo(
    () => ({
      activeTab,
      enableTabsMode,
      tabStates,

      setActiveTab,
      updateFilters,
      addPaginationKey,
      resetTab,

      getCurrentFilters,
      getCurrentPaginationKeys,
      getTabState,
    }),
    [
      activeTab,
      enableTabsMode,
      tabStates,
      setActiveTab,
      updateFilters,
      addPaginationKey,
      resetTab,
      getCurrentFilters,
      getCurrentPaginationKeys,
      getTabState,
    ],
  );
};
